import { Button } from '@mui/material';
import { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import { CreateDeliveryModal } from 'views/Planning/modals/CreateDeliveryModal/CreateDeliveryModal';
import { DeliveryDetailsModal } from 'views/Planning/modals/DeliveryDetailsModal/DeliveryDetailsModal';
import {
    useLazyGetDeviceByDeviceIdEsQuery,
    useLazyGetDeviceTicketQuery,
} from 'store/services';
import _ from 'lodash';
import { Device } from 'types';
import { getObjectValueByPath } from 'helpers/general';
import { useSelector } from 'react-redux';
import { useViewAsSupplierSelector } from 'store/features/viewAsSupplier';
import { useAuth } from 'hooks';

interface DeviceNextDeliveryCellProps {
    device: Device;
    deliveryDate: string;
    refetchListView: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create a component to be used in the renderCell function of the 'Next Delivery' column
export function DeviceNextDeliveryCell({
    device,
    deliveryDate,
    refetchListView,
}: DeviceNextDeliveryCellProps) {
    const [ticketQuery] = useLazyGetDeviceTicketQuery();

    const [deviceQuery] = useLazyGetDeviceByDeviceIdEsQuery();

    const { user } = useAuth();

    const button_label = deliveryDate ? deliveryDate : 'Schedule';

    const [lastActiveDelivery, setLastActiveDelivery] = useState<Ticket | null>(
        device.lastActiveDelivery
    );

    const [ticketUpdateStartedTimestamp, setTicketUpdateStartedTimestamp] =
        useState<number>(0);

    // params we need before calling ticketQuery
    const deviceId = getObjectValueByPath(device, 'deviceId');
    const { viewAsSupplier } = useSelector(useViewAsSupplierSelector);
    const supplierId = viewAsSupplier;

    // flag to track if modal is open or closed
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    // "devicesList", used to auto set device in the create delivery modal
    const devicesList = {
        [device.deviceId]: device,
    };

    // "allDeliveries", used to track if a delivery is created in the modal
    const [allDeliveries, setAllDeliveries] = useState<Ticket[]>([]);

    // device's delivery ticket, passed to the modals
    // fetched and loaded in button click handler before opening modals
    const [modalTicket, setTicketForModal] = useState<Ticket | null>(
        lastActiveDelivery
    );

    // Keeps version of ticket just before calling modal to compare how it changed when modal closes
    const [originalTicket, setOriginalTicket] = useState<Ticket | null>(
        lastActiveDelivery
    );

    // flag to track if ticket was updated in the modal
    const [ticketUpdated, setTicketUpdated] = useState<boolean>(false);
    const [deliveryCreated, setDeliveryCreated] = useState<boolean>(false);

    // flag to check if device has active ticket
    const [isActiveTicket, setIsActiveTicket] = useState<boolean>(false);

    // Fetch device in es, check es_update_time to see if it was updated
    // Keeps checking for update until max time is reached
    const checkForDeviceUpdate = useCallback(async () => {
        const intervalTime = 250;
        const maxTime = 5000;
        let elapsedTime = 0;

        // Fetch device in es every 250ms using an interval
        const interval = setInterval(async () => {
            function endIntervalCallback() {
                clearInterval(interval);
                refetchListView(true);
            }

            const { data, isSuccess } = await deviceQuery({
                user,
                deviceId: deviceId || '',
            });

            if (isSuccess) {
                const device = data.results[0];
                if (device) {
                    const esUpdateTime = device.es_update_time;
                    if (esUpdateTime > ticketUpdateStartedTimestamp) {
                        endIntervalCallback();
                    }
                }
            }

            elapsedTime += intervalTime;
            if (elapsedTime >= maxTime) {
                endIntervalCallback();
            }
        }, intervalTime);
    }, [
        deviceQuery,
        deviceId,
        ticketUpdateStartedTimestamp,
        user,
        refetchListView,
    ]);

    // When criteria is met, trigger device list update by checking for device update
    useEffect(() => {
        if (!isModalOpen) {
            if (ticketUpdated || deliveryCreated) {
                setTicketUpdated(false);
                setDeliveryCreated(false);
                checkForDeviceUpdate();
            }
        }
    }, [isModalOpen, ticketUpdated, deliveryCreated, checkForDeviceUpdate]);

    // When modal opens, set the ticket update timestamp that we can use to check for device updates in es
    useEffect(() => {
        if (isModalOpen) {
            setTicketUpdateStartedTimestamp(Date.now());
        }
    }, [isModalOpen]);

    /*
     * Keep values up to date as ticket state gets updated
     */
    useEffect(() => {
        // Set the active flag so we can check if the ticket is active
        if (lastActiveDelivery?.ticketDirectory === 'active') {
            setIsActiveTicket(true);
        } else {
            setIsActiveTicket(false);
        }
    }, [lastActiveDelivery]);

    /*
     * Handle setting the modal ticket when the active ticket changes
     */
    const handleModalTicketUpdate = (ticket: Ticket | null) => {
        // if the ticket passed in (coming from modal) is different than the original ticket, set the update flag
        if (ticket && !_.isEqual(ticket, originalTicket)) {
            setTicketUpdated(true);
        }

        // only update our ticket states if a valid ticket was passed
        // We keep these the same so we can compare them later for changes
        if (ticket) {
            setTicketForModal(ticket);
            setLastActiveDelivery(ticket);
            setOriginalTicket(ticket);
        }
    };

    /*
     * onClick handler for modal open button
     * - If an active ticket exists, fetch the full ticket from napi and open the edit details modal
     * - If no active ticket exists, open the create ticket modal
     */
    const [buttonClicked, setButtonClicked] = useState<boolean>(false);

    // Handle button click callback
    useEffect(() => {
        if (buttonClicked) {
            /*
             * Function to fetch a device ticket async
             */
            const fetchActiveTicket = async () => {
                // // Call device ticket query and wait for result
                const result = await ticketQuery({
                    deviceId: deviceId || '',
                    supplierId: _.isEmpty(supplierId) ? '' : supplierId,
                    ticketCategory: 'delivery',
                    ticketDirectory: 'active',
                });

                // If the ticket was fetched, return it
                if (result.data) {
                    const ticket = Object.values(result.data)[0]; // result.data looks like: { ticket_id : { ticket }}
                    return ticket;
                } else {
                    return null;
                }
            };

            if (isActiveTicket) {
                // if active ticket on device, fetch the entire ticket from napi
                fetchActiveTicket().then((ticket) => {
                    setTicketForModal(ticket); // update modal ticket
                    setOriginalTicket(ticket); // update original ticket (we use this later to compare changes when modal closes)
                    setIsModalOpen(true); // open edit ticket modal
                    setButtonClicked(false); // reset button clicked flag
                });
            } else {
                // if no active ticket on device, open create ticket modal
                setIsModalOpen(true);
                setButtonClicked(false);
            }
        }
    }, [buttonClicked, deviceId, isActiveTicket, supplierId, ticketQuery]);

    // Handle modal open/close button click
    const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (event) => {
        event.stopPropagation();
        setButtonClicked(true);
    };

    /*
     * When a new delivery is created, refetch the device list once the device has been updated
     */
    useEffect(() => {
        if (allDeliveries.length) {
            setDeliveryCreated(true); // set delivery created flag
            setAllDeliveries([]); // reset all deliveries array
        }
    }, [allDeliveries, isModalOpen]);

    return (
        <div>
            <Button
                variant="outlined"
                color="primary"
                onClick={handleButtonClick}
            >
                {button_label}
            </Button>

            {/* DeliveryDetailsModal - if active ticket and date was clicked*/}
            {modalTicket && isActiveTicket && isModalOpen && (
                <div>
                    <DeliveryDetailsModal
                        modalOpen={isModalOpen}
                        setModalOpen={setIsModalOpen}
                        ticket={modalTicket}
                        device={device}
                        setActiveTicket={handleModalTicketUpdate}
                    />
                </div>
            )}

            {/* CreateDeliveryModal - if no active ticket & schedule was clicked */}
            {!isActiveTicket && isModalOpen && (
                <div>
                    <CreateDeliveryModal
                        modalOpen={isModalOpen}
                        setModalOpen={setIsModalOpen}
                        deliveries={allDeliveries}
                        setAllDeliveries={setAllDeliveries}
                        devicesList={devicesList}
                        setDevicesList={(d) => d}
                        viewAsSupplier={viewAsSupplier}
                    />
                </div>
            )}
        </div>
    );
}
